import type {
    WorkspaceConnectedIntegration,
    WorkspaceDataAccessSnapshot,
    WorkspaceDataRole,
    WorkspaceRoleGrant,
    WorkspaceRoleGrantInput,
    WorkspaceUserRoleAssignment,
    IntegrationPermission,
} from '@common/api/featureService';
import type { Translate } from '@client-app/i18n';
import { AUDIT_LOG_SYSTEM_PERMISSION } from '@/contexts/AuthContext/permissions';
import {
    AccessLevel,
    buildModuleCatalogFromIntegrationPermissions,
    buildDefaultModuleAccess,
    Role,
} from './rolesData';

const PLATFORM_LABELS: Record<string, string> = {
    SHOPIFY: 'Shopify',
    GOOGLE_ADS: 'Google Ads',
    META_ADS: 'Meta Ads',
    FACEBOOK_ADS: 'Meta Ads',
    FACEBOOK_PAGE: 'Facebook Page',
    INSTAGRAM: 'Instagram',
    MAILCHIMP: 'Mailchimp',
    TIKTOK_ADS: 'TikTok Ads',
    GOOGLE_ANALYTICS: 'Google Analytics',
    GOOGLE_SEARCH_CONSOLE: 'Google Search Console',
    BIGCOMMERCE: 'BigCommerce',
    WOOCOMMERCE: 'WooCommerce',
    MAGENTO: 'Magento',
    PRESTASHOP: 'PrestaShop',
    WIX: 'Wix',
    KLAVIYO: 'Klaviyo',
    FEISHU: 'Feishu',
    DINGTALK: 'DingTalk',
    OKENDO: 'Okendo',
    GOAFFPRO: 'GoAffPro',
    UPPROMOTE: 'UpPromote',
    LOOX: 'Loox',
    TRUSTWILL: 'TrustWILL',
    NOXINFLUENCER: 'NoxInfluencer',
};

const normalizePlatformKey = (platform?: string | null) =>
    (platform || '').trim().replace(/[-\s]+/g, '_').toUpperCase();

export const formatIntegrationPlatform = (platform?: string | null) => {
    const key = normalizePlatformKey(platform);
    if (!key) return 'Integration';
    if (PLATFORM_LABELS[key]) return PLATFORM_LABELS[key];

    return key
        .toLowerCase()
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

export const mapBackendAccessToUi = (accessLevel?: string | null): AccessLevel => {
    const value = (accessLevel || '').trim().toUpperCase();
    if (value === 'READ') return 'read';
    if (value === 'WRITE' || value === 'ALL' || value === 'READ_WRITE') return 'action';
    return 'no_access';
};

const roleKey = (value: string) => value.trim().toUpperCase();

const isSystemRole = (value?: string | null) =>
    ['OWNER', 'ADMIN', 'BILLING', 'MEMBER'].includes(roleKey(value || ''));

const isEditableRole = (roleKeyValue?: string | null, defaultRole?: boolean | null, deletable?: boolean | null) => {
    const normalizedRoleKey = roleKey(roleKeyValue || '');
    if (normalizedRoleKey === 'OWNER') return false;
    if (normalizedRoleKey === 'ADMIN') return true;
    return Boolean(defaultRole) || deletable !== false;
};

const isDeletableRole = (roleKeyValue?: string | null, deletable?: boolean | null) => {
    const normalizedRoleKey = roleKey(roleKeyValue || '');
    if (normalizedRoleKey === 'OWNER') return false;
    return Boolean(deletable);
};

const SYSTEM_PERMISSION_GROUPS = {
    canManageWorkspaceSettings: ['workspace_settings'],
    canManageMembers: ['members'],
    canManageRoles: ['roles_access'],
    canManageIntegrations: ['integrations'],
    canManageBilling: ['billing'],
    canViewUsage: ['usage'],
    canViewOutcomeSummary: ['outcome_ledger'],
    canManageOutcomeSummary: ['outcome_ledger:manage'],
    canManageWorkspaceSkills: ['workspace_skill'],
    canManageBrandAccess: ['brand_access'],
    canViewAuditLog: [AUDIT_LOG_SYSTEM_PERMISSION],
} as const;

const SYSTEM_PERMISSION_INPUTS = Object.entries(SYSTEM_PERMISSION_GROUPS)
    .flatMap(([field, permissions]) => permissions.map(permission => ({ field, permission })));

const getConnectionName = (
    connection: WorkspaceConnectedIntegration,
    integrationDisplayName?: string | null
) => connection.storeUrl
    || connection.displayName
    || connection.externalAccountName
    || connection.externalAccountId
    || integrationDisplayName
    || connection.slugId
    || formatIntegrationPlatform(connection.platform);

const getConnectionAccessForRoleFromGrants = (
    grants: WorkspaceRoleGrant[],
    roleId: number,
    connection: WorkspaceConnectedIntegration,
    fallbackAccess: AccessLevel = 'no_access'
) => {
    const grant = grants.find(item =>
        item.roleId === roleId
        && item.integrationId === connection.id
        && (!item.scopeType || item.scopeType === 'CONNECTION'));

    return grant ? mapBackendAccessToUi(grant.accessLevel) : fallbackAccess;
};

const buildConnectionPermissionsFromParts = (
    connections: WorkspaceConnectedIntegration[],
    grants: WorkspaceRoleGrant[],
    roleId: number,
    fallbackAccess: AccessLevel = 'no_access',
    integrationPermissions?: IntegrationPermission[],
    t?: Translate,
) => connections.map((connection) => {
    const brandName = formatIntegrationPlatform(connection.platform);
    const matchingGrant = grants.find(item =>
        item.roleId === roleId
        && item.integrationId === connection.id
        && (!item.scopeType || item.scopeType === 'CONNECTION'));
    const capabilityCatalog = integrationPermissions
        ? buildModuleCatalogFromIntegrationPermissions(brandName, integrationPermissions, t)
        : undefined;

    return {
        connectionId: String(connection.id),
        connectionName: getConnectionName(connection, matchingGrant?.integrationDisplayName),
        connectionLabel: connection.label || null,
        platform: brandName,
        brandName,
        capabilityCatalog,
        moduleAccess: buildDefaultModuleAccess(
            brandName,
            getConnectionAccessForRoleFromGrants(grants, roleId, connection, fallbackAccess),
            integrationPermissions,
            t,
        ),
    };
});

const hasSystemPermissionFromGrants = (
    grants: WorkspaceRoleGrant[],
    roleId: number,
    permissions: readonly string[]
) => permissions.every(permission =>
    grants.some(grant =>
        grant.roleId === roleId
        && grant.scopeType === 'SYSTEM_PERMISSION'
        && grant.permission === permission
        && grant.allowed === true));

const buildSystemPermissionsFromGrants = (grants: WorkspaceRoleGrant[], roleId: number) => ({
    canManageWorkspaceSettings: hasSystemPermissionFromGrants(
        grants,
        roleId,
        SYSTEM_PERMISSION_GROUPS.canManageWorkspaceSettings
    ),
    canManageMembers: hasSystemPermissionFromGrants(grants, roleId, SYSTEM_PERMISSION_GROUPS.canManageMembers),
    canManageRoles: hasSystemPermissionFromGrants(grants, roleId, SYSTEM_PERMISSION_GROUPS.canManageRoles),
    canManageIntegrations: hasSystemPermissionFromGrants(
        grants,
        roleId,
        SYSTEM_PERMISSION_GROUPS.canManageIntegrations
    ),
    canManageConnectionAccess: false,
    canManageBilling: hasSystemPermissionFromGrants(grants, roleId, SYSTEM_PERMISSION_GROUPS.canManageBilling),
    canViewUsage: hasSystemPermissionFromGrants(grants, roleId, SYSTEM_PERMISSION_GROUPS.canViewUsage),
    canViewOutcomeSummary: hasSystemPermissionFromGrants(
        grants,
        roleId,
        SYSTEM_PERMISSION_GROUPS.canViewOutcomeSummary
    ),
    canManageOutcomeSummary: hasSystemPermissionFromGrants(
        grants,
        roleId,
        SYSTEM_PERMISSION_GROUPS.canManageOutcomeSummary
    ),
    canManageWorkspaceSkills: hasSystemPermissionFromGrants(
        grants,
        roleId,
        SYSTEM_PERMISSION_GROUPS.canManageWorkspaceSkills
    ),
    canManageBrandAccess: hasSystemPermissionFromGrants(
        grants,
        roleId,
        SYSTEM_PERMISSION_GROUPS.canManageBrandAccess
    ),
    canViewAuditLog: hasSystemPermissionFromGrants(grants, roleId, SYSTEM_PERMISSION_GROUPS.canViewAuditLog),
    canTransferOwnership: false,
});

const countActiveAssignments = (
    assignments: WorkspaceUserRoleAssignment[],
    roleId: number
) => assignments.filter(item => item.roleId === roleId && item.status !== 'INACTIVE').length;

type WorkspaceDataAccessRoleParts = {
    workspaceId?: string;
    roles: WorkspaceDataRole[];
    userRoles?: WorkspaceUserRoleAssignment[];
    grants?: WorkspaceRoleGrant[];
    connections?: WorkspaceConnectedIntegration[];
    integrationPermissions?: IntegrationPermission[];
};

const mapWorkspaceDataRoleToRole = (
    role: WorkspaceDataRole,
    parts: WorkspaceDataAccessRoleParts,
    t?: Translate,
): Role => {
    const grants = parts.grants ?? [];
    const connections = parts.connections ?? [];
    const userRoles = parts.userRoles ?? [];

    return {
        id: String(role.id),
        name: role.name,
        description: role.description || '',
        roleKey: role.roleKey,
        isSystem: Boolean(role.systemRole) || isSystemRole(role.roleKey) || role.defaultRole || role.roleType === 'SYSTEM_DEFAULT',
        isEditable: isEditableRole(role.roleKey, role.defaultRole, role.deletable),
        isDeletable: isDeletableRole(role.roleKey, role.deletable),
        memberCount: countActiveAssignments(userRoles, role.id),
        permissions: {
            connections: buildConnectionPermissionsFromParts(
                connections,
                grants,
                role.id,
                'no_access',
                parts.integrationPermissions,
                t,
            ),
            ...buildSystemPermissionsFromGrants(grants, role.id),
        },
    };
};

export const applyWorkspaceDataAccessDetailsToRole = (
    role: Role,
    details: {
        grants: WorkspaceRoleGrant[];
        connections: WorkspaceConnectedIntegration[];
        integrationPermissions?: IntegrationPermission[];
    },
    fallbackConnectionAccess: AccessLevel = 'no_access',
    t?: Translate,
): Role => {
    const roleId = Number(role.id);
    const resolvedRoleId = Number.isFinite(roleId) ? roleId : -1;

    return {
        ...role,
        permissions: {
            ...role.permissions,
            connections: buildConnectionPermissionsFromParts(
                details.connections,
                details.grants,
                resolvedRoleId,
                fallbackConnectionAccess,
                details.integrationPermissions,
                t,
            ),
            ...(Number.isFinite(roleId) ? buildSystemPermissionsFromGrants(details.grants, roleId) : {}),
        },
    };
};

export const mapWorkspaceDataAccessPartsToRoles = (
    parts: WorkspaceDataAccessRoleParts,
    t?: Translate,
): Role[] => parts.roles
    .filter(role => role.status !== 'INACTIVE')
    .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))
    .map(role => mapWorkspaceDataRoleToRole(role, parts, t));

export const mapWorkspaceDataAccessSnapshotToRoles = (
    snapshot: WorkspaceDataAccessSnapshot,
    t?: Translate,
): Role[] => mapWorkspaceDataAccessPartsToRoles(snapshot, t);

export const getAssignedDataRoleNameByUserId = (
    assignments: WorkspaceUserRoleAssignment[]
) => new Map(
    assignments
        .filter(item => item.status !== 'INACTIVE')
        .map(item => [item.userId, item.roleName])
);

export const findAssignableDataRole = (roles: Role[], selectedRoleName: string) =>
    roles.find(role =>
        !['OWNER', 'ADMIN'].includes(roleKey(role.name))
        && roleKey(role.name) === roleKey(selectedRoleName)
        && Number.isFinite(Number(role.id)));

export const buildWorkspaceSystemPermissionGrantInputs = (role: Role): WorkspaceRoleGrantInput[] =>
    SYSTEM_PERMISSION_INPUTS.map(({ field, permission }) => ({
        scopeType: 'SYSTEM_PERMISSION',
        permission,
        allowed: Boolean(role.permissions[field as keyof typeof SYSTEM_PERMISSION_GROUPS]),
    }));

export const applyWorkspaceSystemPermissionDetailsToRole = (
    role: Role,
    grants: WorkspaceRoleGrant[]
): Role => {
    const roleId = Number(role.id);

    return {
        ...role,
        permissions: {
            ...role.permissions,
            connections: [],
            ...(Number.isFinite(roleId) ? buildSystemPermissionsFromGrants(grants, roleId) : {}),
        },
    };
};
